import { useState, useEffect, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { Layout } from '../components/Layout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Progress } from '../components/ui/progress';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '../components/ui/alert-dialog';
import { Textarea } from '../components/ui/textarea';
import { 
  ArrowLeft,
  Plus,
  Search,
  Users,
  Building2,
  Target,
  Trash2,
  Upload,
  FileSpreadsheet,
  Check,
  X
} from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Format date from yyyy-mm-dd to dd-mm-yy
const formatDate = (dateStr) => {
  if (!dateStr) return '';
  const parts = dateStr.split('-');
  if (parts.length !== 3) return dateStr;
  const [year, month, day] = parts;
  return `${day}-${month}-${year.slice(-2)}`;
};

export const AssignmentsPage = () => {
  const { projectId } = useParams();
  const [project, setProject] = useState(null);
  const [assignments, setAssignments] = useState([]);
  const [consultants, setConsultants] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  
  // Search filters for the dialog
  const [consultantSearch, setConsultantSearch] = useState('');
  const [companySearch, setCompanySearch] = useState('');
  
  // Simplified: only need consultant and company, objectives/dates come from project
  const [newAssignment, setNewAssignment] = useState({
    consultant_id: '',
    sme_company_id: ''
  });
  const [importData, setImportData] = useState('');

  useEffect(() => {
    fetchData();
  }, [projectId]);

  const fetchData = async () => {
    try {
      const [projectRes, assignmentsRes, consultantsRes, companiesRes] = await Promise.all([
        axios.get(`${API}/projects/${projectId}`),
        axios.get(`${API}/assignments?project_id=${projectId}`),
        axios.get(`${API}/users/consultants`),
        axios.get(`${API}/sme-companies/all`)
      ]);
      setProject(projectRes.data);
      setAssignments(assignmentsRes.data);
      setConsultants(consultantsRes.data);
      setCompanies(companiesRes.data);
    } catch (error) {
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  // Filter consultants based on search
  const filteredConsultants = useMemo(() => {
    if (!consultantSearch.trim()) return consultants;
    const search = consultantSearch.toLowerCase();
    return consultants.filter(c => 
      c.name.toLowerCase().includes(search) || 
      c.email.toLowerCase().includes(search)
    );
  }, [consultants, consultantSearch]);

  // Filter companies based on search
  const filteredCompanies = useMemo(() => {
    if (!companySearch.trim()) return companies;
    const search = companySearch.toLowerCase();
    return companies.filter(c => 
      c.name.toLowerCase().includes(search) ||
      (c.region && c.region.toLowerCase().includes(search)) ||
      (c.contact_person && c.contact_person.toLowerCase().includes(search))
    );
  }, [companies, companySearch]);

  // Get selected consultant/company names
  const selectedConsultant = consultants.find(c => c.id === newAssignment.consultant_id);
  const selectedCompany = companies.find(c => c.id === newAssignment.sme_company_id);

  const handleCreate = async () => {
    if (!newAssignment.consultant_id || !newAssignment.sme_company_id) {
      toast.error('Veuillez sélectionner un consultant et une PME');
      return;
    }

    setSaving(true);
    try {
      // Simplified payload - objectives and dates are inherited from project
      const payload = {
        project_id: projectId,
        consultant_id: newAssignment.consultant_id,
        sme_company_id: newAssignment.sme_company_id,
        min_hours: 0,  // Will be set from project
        min_interventions: 0  // Will be set from project
      };

      await axios.post(`${API}/assignments`, payload);
      toast.success('Affectation créée');
      setDialogOpen(false);
      resetNewAssignment();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la création');
    } finally {
      setSaving(false);
    }
  };

  const resetNewAssignment = () => {
    setNewAssignment({
      consultant_id: '',
      sme_company_id: ''
    });
    setConsultantSearch('');
    setCompanySearch('');
  };

  const handleDelete = async (assignmentId) => {
    try {
      await axios.delete(`${API}/assignments/${assignmentId}`);
      toast.success('Affectation supprimée');
      fetchData();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const handleBulkImport = async () => {
    if (!importData.trim()) {
      toast.error('Veuillez coller des données');
      return;
    }

    setSaving(true);
    try {
      const lines = importData.trim().split('\n');
      const assignments = [];
      
      for (const line of lines) {
        const parts = line.split(/[;\t,]/).map(p => p.trim());
        if (parts.length >= 2) {
          // Simplified: only consultant email and SME name needed
          // Objectives and dates are inherited from project
          assignments.push({
            consultant_email: parts[0],
            sme_name: parts[1]
          });
        }
      }

      const response = await axios.post(`${API}/assignments/bulk-import`, {
        project_id: projectId,
        assignments
      });

      toast.success(response.data.message);
      if (response.data.errors?.length > 0) {
        response.data.errors.forEach(err => toast.error(err));
      }
      
      setImportDialogOpen(false);
      setImportData('');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'import');
    } finally {
      setSaving(false);
    }
  };

  const filteredAssignments = assignments.filter(a =>
    a.consultant_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    a.sme_company_name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin h-8 w-8 border-4 border-slate-900 border-t-transparent rounded-full"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6 fade-in">
        {/* Header */}
        <div className="flex items-center gap-4">
          <Button asChild variant="ghost" size="sm">
            <Link to={`/admin/projects/${projectId}`}>
              <ArrowLeft className="w-4 h-4 mr-1" />
              Dashboard
            </Link>
          </Button>
        </div>

        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-slate-900 font-['Plus_Jakarta_Sans']">
              Affectations
            </h1>
            <p className="text-slate-500">{project?.name} - Gérez les affectations consultant-PME</p>
          </div>
          <div className="flex gap-2">
            <Button 
              variant="outline"
              onClick={() => setImportDialogOpen(true)}
            >
              <Upload className="w-4 h-4 mr-2" />
              Import CSV
            </Button>
            <Button 
              onClick={() => setDialogOpen(true)}
              className="bg-orange-500 hover:bg-orange-600 text-white"
            >
              <Plus className="w-4 h-4 mr-2" />
              Nouvelle affectation
            </Button>
          </div>
        </div>

        {/* Search */}
        <Card className="border-slate-200 shadow-sm">
          <CardContent className="p-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Rechercher par consultant ou PME..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
          </CardContent>
        </Card>

        {/* Assignments Table */}
        <Card className="border-slate-200">
          <CardContent className="p-0">
            {filteredAssignments.length === 0 ? (
              <div className="text-center py-12">
                <Target className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                <p className="text-slate-500">Aucune affectation</p>
                <Button onClick={() => setDialogOpen(true)} className="mt-4">
                  Créer une affectation
                </Button>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50">
                    <TableHead>Consultant</TableHead>
                    <TableHead>PME</TableHead>
                    <TableHead className="text-center">Objectifs</TableHead>
                    <TableHead className="text-center">Réalisé</TableHead>
                    <TableHead>Progression</TableHead>
                    <TableHead className="text-center">Période</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredAssignments.map((assignment) => (
                    <TableRow key={assignment.id}>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Users className="w-4 h-4 text-slate-400" />
                          <span className="font-medium">{assignment.consultant_name}</span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Building2 className="w-4 h-4 text-slate-400" />
                          <span>{assignment.sme_company_name}</span>
                        </div>
                      </TableCell>
                      <TableCell className="text-center">
                        <div className="text-sm">
                          <span className="font-medium">{assignment.target_hours || assignment.min_hours}h</span>
                          <span className="text-slate-400 mx-1">/</span>
                          <span className="font-medium">{assignment.target_interventions || assignment.min_interventions} int.</span>
                        </div>
                      </TableCell>
                      <TableCell className="text-center">
                        <div className="text-sm">
                          <span className="text-emerald-600 font-medium">{assignment.achieved_hours.toFixed(1)}h</span>
                          <span className="text-slate-400 mx-1">/</span>
                          <span className="text-emerald-600 font-medium">{assignment.validated_interventions} int.</span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="space-y-1">
                          <div className="flex items-center gap-2">
                            <Progress value={assignment.progress_hours_percent} className="h-1.5 flex-1" />
                            <span className="text-xs w-10 text-right">{assignment.progress_hours_percent.toFixed(0)}%</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <Progress value={assignment.progress_interventions_percent} className="h-1.5 flex-1" />
                            <span className="text-xs w-10 text-right">{assignment.progress_interventions_percent.toFixed(0)}%</span>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell className="text-center text-sm text-slate-600">
                        {assignment.allowed_start_date && assignment.allowed_end_date ? (
                          <span>{formatDate(assignment.allowed_start_date)} → {formatDate(assignment.allowed_end_date)}</span>
                        ) : (
                          <span className="text-slate-400">-</span>
                        )}
                      </TableCell>
                      <TableCell className="text-right">
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button variant="ghost" size="sm" className="text-red-600 hover:text-red-700">
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>Supprimer cette affectation ?</AlertDialogTitle>
                              <AlertDialogDescription>
                                Cette action supprimera cette affectation. Les interventions existantes seront conservées mais dissociées.
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>Annuler</AlertDialogCancel>
                              <AlertDialogAction onClick={() => handleDelete(assignment.id)} className="bg-red-600 hover:bg-red-700">
                                Supprimer
                              </AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>

        {/* Create Assignment Dialog */}
        <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetNewAssignment(); }}>
          <DialogContent className="sm:max-w-xl max-h-[90vh] flex flex-col">
            <DialogHeader>
              <DialogTitle>Nouvelle affectation</DialogTitle>
              <DialogDescription>
                Affectez un consultant à une PME avec des objectifs
              </DialogDescription>
            </DialogHeader>
            <div className="flex-1 overflow-y-auto space-y-4 py-4 pr-2">
              {/* Consultant Selection */}
              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  <Users className="w-4 h-4" />
                  Consultant *
                </Label>
                {selectedConsultant ? (
                  <div className="flex items-center justify-between p-3 bg-blue-50 border border-blue-200 rounded-lg">
                    <div>
                      <p className="font-medium text-blue-900">{selectedConsultant.name}</p>
                      <p className="text-sm text-blue-700">{selectedConsultant.email}</p>
                    </div>
                    <Button 
                      variant="ghost" 
                      size="sm"
                      onClick={() => setNewAssignment({...newAssignment, consultant_id: ''})}
                      className="text-blue-600 hover:text-blue-800"
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-2">
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                      <Input
                        placeholder="Rechercher un consultant par nom ou email..."
                        value={consultantSearch}
                        onChange={(e) => setConsultantSearch(e.target.value)}
                        className="pl-10"
                      />
                    </div>
                    <div className="max-h-40 overflow-y-auto border border-slate-200 rounded-lg">
                      {filteredConsultants.length === 0 ? (
                        <p className="p-3 text-sm text-slate-500 text-center">Aucun consultant trouvé</p>
                      ) : (
                        filteredConsultants.map((c) => (
                          <div
                            key={c.id}
                            className="flex items-center justify-between p-3 hover:bg-slate-50 cursor-pointer border-b last:border-b-0"
                            onClick={() => setNewAssignment({...newAssignment, consultant_id: c.id})}
                          >
                            <div>
                              <p className="font-medium text-slate-900">{c.name}</p>
                              <p className="text-sm text-slate-500">{c.email}</p>
                            </div>
                            <Check className="w-4 h-4 text-slate-300" />
                          </div>
                        ))
                      )}
                    </div>
                    <p className="text-xs text-slate-500">{filteredConsultants.length} consultant(s) trouvé(s)</p>
                  </div>
                )}
              </div>

              {/* Company Selection */}
              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  <Building2 className="w-4 h-4" />
                  PME *
                </Label>
                {selectedCompany ? (
                  <div className="flex items-center justify-between p-3 bg-emerald-50 border border-emerald-200 rounded-lg">
                    <div>
                      <p className="font-medium text-emerald-900">{selectedCompany.name}</p>
                      <p className="text-sm text-emerald-700">
                        {selectedCompany.region && `${selectedCompany.region} • `}
                        {selectedCompany.contact_person || 'Pas de contact'}
                      </p>
                    </div>
                    <Button 
                      variant="ghost" 
                      size="sm"
                      onClick={() => setNewAssignment({...newAssignment, sme_company_id: ''})}
                      className="text-emerald-600 hover:text-emerald-800"
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-2">
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                      <Input
                        placeholder="Rechercher une PME par nom, région ou contact..."
                        value={companySearch}
                        onChange={(e) => setCompanySearch(e.target.value)}
                        className="pl-10"
                      />
                    </div>
                    <div className="max-h-40 overflow-y-auto border border-slate-200 rounded-lg">
                      {filteredCompanies.length === 0 ? (
                        <p className="p-3 text-sm text-slate-500 text-center">Aucune PME trouvée</p>
                      ) : (
                        filteredCompanies.map((c) => (
                          <div
                            key={c.id}
                            className="flex items-center justify-between p-3 hover:bg-slate-50 cursor-pointer border-b last:border-b-0"
                            onClick={() => setNewAssignment({...newAssignment, sme_company_id: c.id})}
                          >
                            <div>
                              <p className="font-medium text-slate-900">{c.name}</p>
                              <p className="text-sm text-slate-500">
                                {c.region && <Badge variant="outline" className="mr-2 text-xs">{c.region}</Badge>}
                                {c.contact_person || 'Pas de contact'}
                              </p>
                            </div>
                            <Check className="w-4 h-4 text-slate-300" />
                          </div>
                        ))
                      )}
                    </div>
                    <p className="text-xs text-slate-500">{filteredCompanies.length} PME trouvée(s)</p>
                  </div>
                )}
              </div>

              {/* Objectives */}
              {/* Info: objectives and dates inherited from project */}
              <div className="pt-4 border-t">
                <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
                  <p className="text-sm text-blue-800 font-medium mb-1">
                    Configuration héritée du projet
                  </p>
                  <p className="text-xs text-blue-600">
                    Les objectifs (heures et interventions minimum) ainsi que la période sont automatiquement 
                    appliqués depuis les paramètres du projet.
                  </p>
                  {project && (
                    <div className="mt-2 text-xs text-blue-700 space-y-1">
                      <p>• Heures min: <strong>{project.min_hours_per_assignment || 0}h</strong></p>
                      <p>• Interventions min: <strong>{project.min_interventions_per_assignment || 0}</strong></p>
                      <p>• Période: <strong>{formatDate(project.start_date)}</strong> → <strong>{formatDate(project.end_date)}</strong></p>
                    </div>
                  )}
                </div>
              </div>
            </div>
            <DialogFooter className="border-t pt-4">
              <Button variant="outline" onClick={() => setDialogOpen(false)}>Annuler</Button>
              <Button onClick={handleCreate} disabled={saving} className="bg-slate-900 hover:bg-slate-800">
                {saving ? 'Création...' : 'Créer l\'affectation'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Import Dialog */}
        <Dialog open={importDialogOpen} onOpenChange={setImportDialogOpen}>
          <DialogContent className="sm:max-w-lg">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <FileSpreadsheet className="w-5 h-5" />
                Import en masse
              </DialogTitle>
              <DialogDescription>
                Collez vos données CSV (email consultant; nom PME)
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <Textarea
                placeholder="consultant@email.com;Nom PME
consultant2@email.com;Autre PME"
                value={importData}
                onChange={(e) => setImportData(e.target.value)}
                rows={10}
                className="font-mono text-sm"
              />
              <p className="text-xs text-slate-500">
                Séparateurs acceptés: point-virgule (;), virgule (,) ou tabulation.
                Les objectifs et dates sont hérités du projet.
              </p>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setImportDialogOpen(false)}>Annuler</Button>
              <Button onClick={handleBulkImport} disabled={saving} className="bg-slate-900 hover:bg-slate-800">
                {saving ? 'Import...' : 'Importer'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};
